import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import { Invoice, ArchiveEntry } from '@/types/invoice'

// State interface
interface InvoiceState {
  invoices: Invoice[]
  isLoading: boolean
  error: string | null
  selectedInvoice: Invoice | null
  emailTemplate: EmailTemplate
  archive: ArchiveEntry[]
}

// Email template interface
interface EmailTemplate {
  id: string
  name: string
  subject: string
  body: string
  isDefault?: boolean
}

// Action types
type InvoiceAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_INVOICES'; payload: Invoice[] }
  | { type: 'ADD_INVOICE'; payload: Invoice }
  | { type: 'UPDATE_INVOICE'; payload: { id: string; updates: Partial<Invoice> } }
  | { type: 'DELETE_INVOICE'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SELECT_INVOICE'; payload: Invoice | null }
  | { type: 'UPDATE_EMAIL_TEMPLATE'; payload: Partial<EmailTemplate> }
  | { type: 'ADD_TO_ARCHIVE'; payload: ArchiveEntry }
  | { type: 'UPDATE_ARCHIVE_ENTRY'; payload: { id: string; updates: Partial<ArchiveEntry> } }
  | { type: 'RESTORE_FROM_ARCHIVE'; payload: { archiveId: string; invoice: Invoice } }

// Initial state
const initialState: InvoiceState = {
  invoices: [],
  isLoading: false,
  error: null,
  selectedInvoice: null,
  emailTemplate: {
    id: 'default-template',
    name: 'Default Template',
    subject: 'Invoice Reminder - {client}',
    body: `Dear {client},

This is a friendly reminder that invoice {number} for {amount} {currency} is due on {due_date}.

Please process this payment at your earliest convenience.

Best regards,
Your Company`,
    isDefault: true
  },
  archive: []
}

// Reducer function
function invoiceReducer(state: InvoiceState, action: InvoiceAction): InvoiceState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_INVOICES':
      return { ...state, invoices: action.payload, error: null }
    
    case 'ADD_INVOICE':
      return { ...state, invoices: [...state.invoices, action.payload] }
    
    case 'UPDATE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map(invoice =>
          invoice.number === action.payload.id
            ? { ...invoice, ...action.payload.updates }
            : invoice
        )
      }
    
    case 'DELETE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.filter(invoice => invoice.number !== action.payload)
      }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'SELECT_INVOICE':
      return { ...state, selectedInvoice: action.payload }
    
    case 'UPDATE_EMAIL_TEMPLATE':
      return { 
        ...state, 
        emailTemplate: { ...state.emailTemplate, ...action.payload }
      }
    
    case 'ADD_TO_ARCHIVE':
      return {
        ...state,
        archive: [action.payload, ...state.archive]
      }
    
    case 'UPDATE_ARCHIVE_ENTRY':
      return {
        ...state,
        archive: state.archive.map(entry =>
          entry.id === action.payload.id
            ? { ...entry, ...action.payload.updates }
            : entry
        )
      }
    
    case 'RESTORE_FROM_ARCHIVE':
      return {
        ...state,
        invoices: [...state.invoices, action.payload.invoice],
        archive: state.archive.filter(entry => entry.id !== action.payload.archiveId)
      }
    
    default:
      return state
  }
}

// Context interface
interface InvoiceContextType {
  state: InvoiceState
  dispatch: React.Dispatch<InvoiceAction>
  // Convenience methods
  setInvoices: (invoices: Invoice[]) => void
  addInvoice: (invoice: Invoice) => void
  updateInvoice: (id: string, updates: Partial<Invoice>) => void
  deleteInvoice: (id: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  selectInvoice: (invoice: Invoice | null) => void
  clearError: () => void
  updateEmailTemplate: (updates: Partial<EmailTemplate>) => void
  addToArchive: (entry: ArchiveEntry) => void
  updateArchiveEntry: (id: string, updates: Partial<ArchiveEntry>) => void
  restoreFromArchive: (archiveId: string, invoice: Invoice) => void
}

// Create context
const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined)

// Provider component
interface InvoiceProviderProps {
  children: ReactNode
}

export function InvoiceProvider({ children }: InvoiceProviderProps) {
  const [state, dispatch] = useReducer(invoiceReducer, initialState)

  // Convenience methods
  const setInvoices = (invoices: Invoice[]) => {
    dispatch({ type: 'SET_INVOICES', payload: invoices })
  }

  const addInvoice = (invoice: Invoice) => {
    dispatch({ type: 'ADD_INVOICE', payload: invoice })
  }

  const updateInvoice = (id: string, updates: Partial<Invoice>) => {
    dispatch({ type: 'UPDATE_INVOICE', payload: { id, updates } })
  }

  const deleteInvoice = (id: string) => {
    dispatch({ type: 'DELETE_INVOICE', payload: id })
  }

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading })
  }

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }

  const selectInvoice = (invoice: Invoice | null) => {
    dispatch({ type: 'SELECT_INVOICE', payload: invoice })
  }

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null })
  }

  const updateEmailTemplate = (updates: Partial<EmailTemplate>) => {
    dispatch({ type: 'UPDATE_EMAIL_TEMPLATE', payload: updates })
  }

  const addToArchive = (entry: ArchiveEntry) => {
    dispatch({ type: 'ADD_TO_ARCHIVE', payload: entry })
  }

  const updateArchiveEntry = (id: string, updates: Partial<ArchiveEntry>) => {
    dispatch({ type: 'UPDATE_ARCHIVE_ENTRY', payload: { id, updates } })
  }

  const restoreFromArchive = (archiveId: string, invoice: Invoice) => {
    dispatch({ type: 'RESTORE_FROM_ARCHIVE', payload: { archiveId, invoice } })
  }

  const value: InvoiceContextType = {
    state,
    dispatch,
    setInvoices,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    setLoading,
    setError,
    selectInvoice,
    clearError,
    updateEmailTemplate,
    addToArchive,
    updateArchiveEntry,
    restoreFromArchive
  }

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  )
}

// Custom hook
export function useInvoice() {
  const context = useContext(InvoiceContext)
  if (context === undefined) {
    throw new Error('useInvoice must be used within an InvoiceProvider')
  }
  return context
} 